import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useNavigate } from 'react-router-dom'
import type { BellNotification } from '../lib/types'
import { getNotificationRoute } from '../lib/types'
import { useMarkRead } from '../api/notification-mutations'

const MONGO_OBJECT_ID_HEX24 = /^[a-fA-F0-9]{24}$/

function isValidMongoObjectIdString(id: string | undefined): id is string {
  return typeof id === 'string' && MONGO_OBJECT_ID_HEX24.test(id.trim())
}

interface Props {
  readonly notification: BellNotification
  readonly onClose: () => void
}

function timeAgo(isoDate: string | null): string {
  if (!isoDate) return ''
  const diff = Date.now() - new Date(isoDate).getTime()
  const minutes = Math.floor(diff / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

export function NotificationItem({ notification, onClose }: Props) {
  const navigate = useNavigate()
  const { mutate: markRead } = useMarkRead()

  const route = getNotificationRoute(notification)
  const isClickable = route !== null

  function handleClick() {
    if (!notification.isRead && isValidMongoObjectIdString(notification.id)) {
      markRead(notification.id)
    }
    if (route) {
      navigate(route)
      onClose()
    }
  }

  return (
    <Box
      data-testid={`notification-item-${notification.id}`}
      onClick={handleClick}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        px: '16px',
        py: '12px',
        cursor: isClickable ? 'pointer' : 'default',
        borderBottom: '1px solid #f0f0f0',
        transition: 'background-color 150ms ease',
        '&:hover': isClickable
          ? { bgcolor: 'rgba(0,140,208,0.04)' }
          : { bgcolor: 'rgba(0,0,0,0.02)' },
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      {/* Unread indicator — 8px dot at #008cd0, space-holding when read */}
      <Box
        sx={{
          flexShrink: 0,
          width: '8px',
          height: '14px',
          display: 'flex',
          alignItems: 'flex-start',
          pt: '3px',
        }}
      >
        {!notification.isRead && (
          <Box
            sx={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              bgcolor: '#008cd0',
            }}
          />
        )}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <Typography
          component="p"
          sx={{
            fontSize: '14px',
            fontWeight: notification.isRead ? 400 : 600,
            lineHeight: '20px',
            color: '#0f1729',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            wordBreak: 'break-word',
          }}
        >
          {notification.message}
        </Typography>

        {/* Timestamp */}
        <Typography
          component="p"
          sx={{
            fontSize: '10px',
            fontWeight: 400,
            lineHeight: '16px',
            color: '#3b82f6',
          }}
        >
          {timeAgo(notification.createdAt)}
        </Typography>
      </Box>
    </Box>
  )
}
